import React, { useEffect, useRef, useState } from 'react';
import TimesheetOfflineBanner from '../../common/TimesheetOfflineBanner';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { CircularProgress } from '@mui/material';
import TNEDashboardHeader from '../../common/TNEDashboardHeader/TNEDashboardHeader';
import { deleteTimesheet, getNextTimesheets, getTimesheets } from '../../../actions/timesheets';
import CustomTable from '../../common/Table/CustomTable/CustomTable';
import { columns } from './helpers';
import {
  globalPageSize,
  responsiveSizeTimesheet,
  contractorFilters,
  approverFilters,
  appAreas,
  appNotificationsTypes
} from '../../../utils/constants';
import { useOnScreen, usePrevious, useQuery, useWindowSize } from '../../../utils/hooks';
import TNECardsList from '../../common/TNECardsList/TNECardsList';
import AddGradientIcon from '../../common/Icons/AddGradientIcon';
import StyledPaginationPanel from '../../common/StyledPaginationPanel/StyledPaginationPanel';
import classes from './Timesheets.module.scss';
import MainButton from '../../common/buttons/MainButton';
import { clearTimesheet, getTimeExpenseValidation, getTandEPendingCounts } from '../../../actions/timesheet';
import { basicErrorHandler } from '../../../utils/helpers';
import colors from '../../../scss/colors';
import TnEListSkeleton from '../../common/Skeletons/TnEListSkeleton';
import { getOptionsForFilters } from '../../../actions/filtersOptionsFromApi';
import WithCloseIcon from '../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import BaseModal from '../../common/ModalWindows/BaseModal';
import { DeletePopUp, CopyModal } from '../../common/ModalWindows/TNEModals/index';
import {
  markTimesheetNotificationsRead,
  removeNotification,
  setNotification,
  shouldReadTimesheetsNotification
} from '../../../actions/appNotifications';
import { tneAxiosClient } from '../../../api/apiSettings';
import { TNE_SORT_BY, generalEnums } from '../../../utils/enums';
import AlternateMessageDialog from '../../common/AlternateMessageDialogComponent/AlternateMessageDialog';
import SuccessIcon from '../../common/Icons/SuccessIcon';

export default function Timesheets() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const size = useWindowSize();
  const query = useQuery();

  const initIntersactionRequestRef = useRef(true);
  const IntersactionMarkerRef = useRef(null);
  const isIntersecting = useOnScreen(IntersactionMarkerRef);

  // Selectors
  const timesheetsList = useSelector((state) => state.timesheets.data);
  const paginationData = useSelector((state) => state.timesheets.pagination);
  const infinityIsLoading = useSelector((state) => state.timesheets.pendingInfinityScroll);
  const timesheetsPagination = useSelector((state) => state.timesheets.pagination);
  const timesheetsPending = useSelector((state) => state.timesheets.pending);
  const deletePending = useSelector((state) => state.timesheets.pendingDelete);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const timesheetValidationStatus = useSelector((state) => state?.timesheet?.validate?.timesheet.IsValidAdminSettings);
  const timesheetPlacementStatus = useSelector((state) => state?.timesheet?.validate?.timesheet.IsValidPlacementStatus);
  const filtersOptions = useSelector((state) => state.filtersOptionsFromApi.filters);
  const hasTimesheetPendingCount = useSelector((state) => state.timesheet.hasTimesheetPendingCount);
  const isTimesheetRead = useSelector((state) => state.appNotifications.isTimesheetRead);
  const isOnline = useSelector((state) => state.pwa.isOnline);

  const filters = isApprover ? approverFilters : contractorFilters;
  const initPageNumber = Number(query.get('page')) || 1;
  const initStartDate = query.get('start') || '';
  const initMonth = query.get('month') || '';
  const initYear = query.get('year') || '';
  const initLocation = query.get('location') || '';
  const initStatus = query.get('status') || '';
  const initFirstName = query.get('FirstName') || '';
  const initLastName = query.get('LastName') || '';
  const initSortBy = query.get('sortby') || TNE_SORT_BY.PERIOD; // Remove [ ]
  const [sortby, setSortBy] = useState([initSortBy]);
  const [active, setCurrentPage] = useState(initPageNumber);
  const [start, setStart] = useState(initStartDate);
  const [month, setMonth] = useState(initMonth);
  const [year, setYear] = useState(initYear);
  const [location, setLocation] = useState(initLocation);
  const [status, setStatus] = useState(initStatus);
  const [contractor, setContractor] = useState({
    FirstName: initFirstName,
    LastName: initLastName
  });
  const [openPopUp, setOpenPop] = useState(false);
  const [tsFordelete, setTSfordelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const prevPendingDelete = usePrevious(deletePending);
  const browserLocation = useLocation();
  const itemClickHandler = (id) => navigate(`/dashboard/timesheet/${id}${browserLocation.search}`);
  const [isFocus, setFocus] = useState(null);

  const itemFocusInHandler = (id) => {
    setFocus(id);
  };

  const itemFocusOutHandler = (id) => {
    setFocus(!id);
  };

  const userId = localStorage.getItem('UserId');

  const handleSearchParams = () => {
    let oldSearchParams = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of searchParams.entries()) {
      oldSearchParams = {
        ...oldSearchParams,
        [key]: value
      };
    }

    return setSearchParams({
      ...oldSearchParams,
      page: active,
      start,
      month,
      year,
      location,
      status,
      sortby,
      FirstName: contractor.FirstName,
      LastName: contractor.LastName
    });
  };

  const deleteHandler = (id) => {
    setOpenPop({ modalType: 'delete' });
    setTSfordelete(id);
  };

  const finalDeletingHandler = () => {
    dispatch(deleteTimesheet(tsFordelete));
  };

  const copyHandler = (id) => {
    setOpenPop({ modalType: 'copy', params: { timesheet: timesheetsList.find((ts) => ts.Id === id) } });
  };

  const handlePagination = (num) => {
    setCurrentPage(num);
  };

  const handleMonth = (choosedMonth) => {
    setMonth(choosedMonth);
    if (choosedMonth === t('common.general.all')) setYear('');
    if (choosedMonth && choosedMonth !== t('common.general.all')) setStart('');
  };

  const handleYear = (choosedYear, chosenMonth) => {
    setYear(choosedYear);
    dispatch(
      getOptionsForFilters(
        appAreas.TIMESHEETS,
        isApprover,
        `${choosedYear}-${chosenMonth || '01'}-01`,
        location,
        status,
        '',
        start
      )
    );
  };

  const handleWeek = (choosedStart) => {
    setStart(choosedStart);
    if (choosedStart) handleMonth('');
    dispatch(
      getOptionsForFilters(
        appAreas.TIMESHEETS,
        isApprover,
        choosedStart ? dayjs(choosedStart).format(generalEnums.DATEFORMAT) : '',
        location,
        status,
        '',
        dayjs(choosedStart).format(generalEnums.DATEFORMAT)
      )
    );
  };

  const handleLocation = (choosedLocation) => {
    setLocation(choosedLocation);
    dispatch(
      getOptionsForFilters(
        appAreas.TIMESHEETS,
        isApprover,
        year || month ? `${year}-${month || '01'}-01` : '',
        choosedLocation,
        status,
        '',
        start
      )
    );
  };

  const handleStatus = (choosedStatus) => {
    setStatus(choosedStatus);
    dispatch(
      getOptionsForFilters(
        appAreas.TIMESHEETS,
        isApprover,
        year || month ? `${year}-${month || '01'}-01` : '',
        location,
        choosedStatus,
        '',
        start
      )
    );
  };

  const handleSortBy = (choosedSortBy) => {
    setSortBy(choosedSortBy);
  };

  const handleContractor = (choosedContractor) => {
    setContractor(choosedContractor);
  };

  useEffect(() => {
    setFocus(isFocus);
  }, [isFocus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [start, sortby, month, year, location, status, contractor]);

  useEffect(() => {
    dispatch(
      getTimesheets(
        userId,
        active,
        globalPageSize,
        start,
        month,
        year,
        location,
        status,
        contractor,
        sortby,
        t,
        isApprover,
        filtersOptions
      )
    );
    handleSearchParams();
  }, [active, start, month, year, location, status, contractor, sortby]);

  useEffect(() => {
    dispatch(clearTimesheet());
    dispatch(getTimeExpenseValidation('', ''));
  }, []);

  useEffect(() => {
    if (isApprover) {
      dispatch(getTandEPendingCounts());
    }
  }, [isApprover, timesheetsList]);

  useEffect(() => {
    dispatch(getOptionsForFilters(appAreas.TIMESHEETS, isApprover, ''));
  }, []);

  useEffect(() => {
    if (
      isIntersecting &&
      size.width < responsiveSizeTimesheet &&
      !initIntersactionRequestRef.current &&
      paginationData &&
      paginationData.PageNumber + 1 <= paginationData?.TotalPages
    ) {
      dispatch(
        getNextTimesheets(
          userId,
          Number(paginationData.PageNumber) + 1,
          globalPageSize,
          start,
          month,
          year,
          location,
          status,
          contractor,
          sortby,
          t,
          isApprover,
          filtersOptions
        )
      );
    }
    initIntersactionRequestRef.current = false;
  }, [isIntersecting]);

  useEffect(() => {
    if ((prevPendingDelete && !deletePending) || browserLocation.state?.idToDelete) {
      setOpenPop(false);
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${t('dashboard.timesheet.timesheet')} ${t('common.general.deleted')}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS,
          bottom: '160px',
          action: (
            <p
              role="presentation"
              className={classes.undoBtn}
              onClick={async () => {
                try {
                  // implement call to API to restore timesheet by id using tsFordelete
                  await tneAxiosClient.get(
                    `/v1/timesheet/activate?timesheetId=${browserLocation.state?.idToDelete || tsFordelete}`
                  );
                  dispatch(
                    getTimesheets(
                      userId,
                      active,
                      globalPageSize,
                      start,
                      month,
                      year,
                      location,
                      status,
                      contractor,
                      sortby,
                      t,
                      isApprover,
                      filtersOptions
                    )
                  );
                  dispatch(removeNotification());
                } catch (e) {
                  basicErrorHandler(dispatch, e.message);
                }
              }}
            >
              {t('common.general.undo')}
            </p>
          )
        })
      );
    }
  }, [prevPendingDelete, deletePending]);

  useEffect(() => {
    if (isTimesheetRead.enabled && timesheetsList) {
      dispatch(markTimesheetNotificationsRead(userId, isTimesheetRead.status));
      dispatch(shouldReadTimesheetsNotification({ enabled: false, status: null }));
    }
  }, [isTimesheetRead?.enabled, timesheetsList]);

  const popUpModals = {
    copy: <CopyModal timesheet={openPopUp?.params?.timesheet} withExternalRedirect={false} />,
    delete: (
      <DeletePopUp
        deleteHandler={finalDeletingHandler}
        closeHandler={() => setOpenPop(false)}
        withBackbtn={false}
        pending={deletePending}
      />
    )
  };

  const dateParser = (date) => {
    if (start) return start;
    if (year || month) return `${year}-${month}-01`;

    return date || '';
  };

  const dateBackAction = (date, locationParser = '', statusParser = '') => {
    dispatch(
      getOptionsForFilters(
        appAreas.TIMESHEETS,
        isApprover,
        dateParser(date),
        locationParser || location,
        statusParser || status,
        '',
        start
      )
    );
  };

  const dateForwardAction = (date) => {
    dispatch(getOptionsForFilters(appAreas.TIMESHEETS, isApprover, date || ''));
  };

  if (hasTimesheetPendingCount && status === 'Pending') {
    return (
      <section className={classes.mainContainer}>
        <Box sx={{ mb: 1 }}>
          <TimesheetOfflineBanner context="list" />
        </Box>
        <TNEDashboardHeader
          title={size.width < responsiveSizeTimesheet ? t('dashboard.timesheets.mobileTitle') : ''}
          filters={filters}
          values={{
            start,
            month,
            year,
            location,
            status,
            contractor,
            sortby
          }}
          changeHandlers={{
            handleWeek,
            handleMonth,
            handleYear,
            handleLocation,
            handleStatus,
            handleContractor,
            handleSortBy,
            dateBackAction,
            dateForwardAction
          }}
        />
        <AlternateMessageDialog
          icon={<SuccessIcon />}
          headerLabel={t('dashboard.timesheets.greatJob')}
          startTracking={
            !isApprover
              ? t('dashboard.createtimesheet.starttrackingnow')
              : t('dashboard.createtimesheet.timesheetawaitapproval')
          }
          mainButtonLabel={t('dashboard.timesheets.showalltimesheets')}
          mainButtonEvent={() => setStatus('')}
          isApprover={isApprover}
        />
      </section>
    );
  }

  return (
    <section className={classes.mainContainer}>
      <TNEDashboardHeader
        title={size.width < responsiveSizeTimesheet ? t('dashboard.timesheets.mobileTitle') : ''}
        filters={filters}
        values={{
          start,
          month,
          year,
          location,
          status,
          contractor,
          sortby
        }}
        changeHandlers={{
          handleWeek,
          handleMonth,
          handleYear,
          handleLocation,
          handleStatus,
          handleContractor,
          handleSortBy,
          dateBackAction,
          dateForwardAction
        }}
      />
      {size.width > responsiveSizeTimesheet ? (
        <CustomTable
          data={timesheetsList || []}
          columns={() =>
            columns(t, navigate, copyHandler, deleteHandler, isApprover, timesheetPlacementStatus, isFocus)
          }
          withFooter={false}
          withLines={{
            header: false,
            body: false,
            footer: false
          }}
          bodyWithBorderRadius={false}
          rowClickHandler={itemClickHandler}
          rowFocusInHandler={itemFocusInHandler}
          rowFocusOutHandler={itemFocusOutHandler}
          wrapperStyles={{}}
          headerStyles={{
            borderRadius: '10px  10px 10px 10px',
            padding: '20px 40px 20px 88px'
          }}
          bodyStyles={{
            overflow: 'visible'
          }}
          lineStyles={{
            backgroundColor: colors.WHITE,
            boxShadow: '0 0 30px rgba(61, 107, 149, 0.1)',
            borderRadius: '10px',
            height: '68px',
            marginTop: '12px',
            padding: '20px 40px 20px 88px'
          }}
          isLoading={timesheetsList === null}
          loadingComponent={<TnEListSkeleton qty={6} height="68px" gap="12px" />}
          isApprover={isApprover}
        />
      ) : (
        <div className={classes.cardListWrapper}>
          <TNECardsList
            type="timesheet"
            data={timesheetsList}
            clickHandler={itemClickHandler}
            isLoading={timesheetsList === null}
            loadingComponent={<TnEListSkeleton qty={10} height="90px" gap="16px" />}
          />
        </div>
      )}
      <div ref={IntersactionMarkerRef}>
        {infinityIsLoading && <CircularProgress sx={{ color: colors.NAVY_BLUE }} />}
      </div>
      {!isApprover && timesheetPlacementStatus && (
        <div className={classes.addButtonContainer}>
          <Link to="/dashboard/create-timesheet">
            <div className={classes.innerContainer}>
              {size.width > responsiveSizeTimesheet ? (
                <MainButton
                  rounded
                  forceWidth="max-content"
                  size="medium"
                  variant={timesheetValidationStatus ? 'primary' : 'disabled'}
                  disabled={!timesheetValidationStatus}
                >
                  {t('dashboard.timesheets.newbuttontxt')}
                </MainButton>
              ) : (
                <AddGradientIcon size="50px" />
              )}
            </div>
          </Link>
        </div>
      )}
      {size.width > responsiveSizeTimesheet && (
        <div className={`${classes.paginationPanelContainer} ${isApprover ? classes.withApprover : ''}`}>
          <StyledPaginationPanel
            total={timesheetsPagination?.TotalPages}
            active={active}
            setActive={(num) => handlePagination(num)}
          />
        </div>
      )}
      <BaseModal
        open={!!openPopUp}
        fullscreen={false}
        closeHandler={() => setOpenPop(null)}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        marginLeftRight={3}
        backgroundColor={colors.PAGE_BACKGROUND}
        paperWidth={size.width > responsiveSizeTimesheet ? undefined : '100%'}
        paperStyles={
          size.width <= responsiveSizeTimesheet ? { margin: '15px', borderRadius: '10px' } : { borderRadius: '20px' }
        }
      >
        <WithCloseIcon close={() => setOpenPop(null)}>{popUpModals[openPopUp?.modalType]}</WithCloseIcon>
      </BaseModal>
    </section>
  );
}
